import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updateStockAsync, fetchStockListAsync } from "../../../../../store/Slice/stockSlice";
import { Package, User, CheckCircle, Hash, FileText, AlertCircle } from "lucide-react";
import type { AppDispatch } from "../../../../../store/store";

type StockType = {
  _id?: string;
  instrument?: { name?: string; code?: string } | null;
  responsiblePerson?: string | null;
  approvedBy?: string | null;
  quantity?: number | null;
  remark?: string | null;
  parameters?: string | null;
};

interface Props {
  stock?: StockType | null;
  onClose: () => void;
  onSaved?: () => void;
}

const StockEditForm: React.FC<Props> = ({ stock, onClose, onSaved }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [instrumentName, setInstrumentName] = useState("");
  const [instrumentCode, setInstrumentCode] = useState("");
  const [responsiblePerson, setResponsiblePerson] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [quantity, setQuantity] = useState("");
  const [remark, setRemark] = useState("");
  const [parameters, setParameters] = useState("");
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (stock) {
      setInstrumentName(stock.instrument?.name || "");
      setInstrumentCode(stock.instrument?.code || "");
      setResponsiblePerson(stock.responsiblePerson || "");
      setApprovedBy(stock.approvedBy || "");
      setQuantity(String(stock.quantity || ""));
      setRemark(stock.remark || "");
      setParameters(stock.parameters || "");
    }
  }, [stock]);

  useEffect(() => {
    if (stock) {
      const changed = 
        instrumentName !== (stock.instrument?.name || "") ||
        instrumentCode !== (stock.instrument?.code || "") ||
        responsiblePerson !== (stock.responsiblePerson || "") ||
        approvedBy !== (stock.approvedBy || "") ||
        quantity !== String(stock.quantity || "") ||
        remark !== (stock.remark || "") ||
        parameters !== (stock.parameters || "");
      setHasChanges(changed);
    }
  }, [instrumentName, instrumentCode, responsiblePerson, approvedBy, quantity, remark, parameters, stock]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!instrumentName.trim()) {
      newErrors.instrumentName = "Instrument name is required";
    }
    
    if (!instrumentCode.trim()) {
      newErrors.instrumentCode = "Instrument code is required";
    }
    
    if (!quantity || Number(quantity) < 0) {
      newErrors.quantity = "Valid quantity is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (!stock?._id) return;
    
    if (!validateForm()) {
      toast.error("Please fix the errors before saving");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        instrument: { name: instrumentName, code: instrumentCode },
        responsiblePerson,
        approvedBy,
        quantity: Number(quantity) || 0,
        remark,
        parameters,
      };

      await dispatch(updateStockAsync({ id: stock._id, payload })).unwrap();
      toast.success("Stock updated successfully", { toastId: `stock-updated-${stock._id}` });
      dispatch(fetchStockListAsync({}));
      setSaving(false);
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      setSaving(false);
      const errMsg = (err && typeof err === "object" && "message" in err)
        ? (err as { message?: string }).message
        : String(err ?? "Update failed");
      toast.error(errMsg || "Update failed");
    }
  };

  const handleReset = () => {
    if (stock) {
      setInstrumentName(stock.instrument?.name || "");
      setInstrumentCode(stock.instrument?.code || "");
      setResponsiblePerson(stock.responsiblePerson || "");
      setApprovedBy(stock.approvedBy || "");
      setQuantity(String(stock.quantity || ""));
      setRemark(stock.remark || "");
      setParameters(stock.parameters || "");
      setErrors({});
    }
  };

  return (
    <div className="w-full max-w-[850px] h-full max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden flex flex-col border border-gray-300">
      {/* Header */}
      <div className="bg-secondary px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
            <Package className="w-5 h-5 text-black" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-black">Edit Stock Item</h3>
      
          </div>
        </div>
      </div>

      {/* Change Indicator */}
      {hasChanges && (
        <div className="bg-gray-100 border-l-4 border-gray-800 px-6 py-2 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-gray-800" />
          <span className="text-sm text-gray-800 font-medium">You have unsaved changes</span>
        </div>
      )}

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="grid grid-cols-2 gap-5">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Instrument Details */}
            <div className="bg-white rounded-lg p-4 border border-gray-300">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-900" />
                Instrument Details
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block">
                    Instrument Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={instrumentName} 
                    onChange={(e) => setInstrumentName(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                      errors.instrumentName ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    placeholder="e.g., Digital Thermometer"
                  />
                  {errors.instrumentName && (
                    <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.instrumentName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block">
                    Instrument Code <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={instrumentCode} 
                    onChange={(e) => setInstrumentCode(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                      errors.instrumentCode ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    placeholder="e.g., INST-001"
                  />
                  {errors.instrumentCode && (
                    <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.instrumentCode}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block flex items-center gap-1">
                    <Hash className="w-4 h-4 text-gray-600" />
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={quantity} 
                    onChange={(e) => setQuantity(e.target.value)}
                    type="number"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all ${
                      errors.quantity ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'
                    }`}
                    placeholder="Enter quantity"
                  />
                  {errors.quantity && (
                    <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.quantity}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Personnel Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-300">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-gray-900" />
                Personnel Information
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block">
                    Responsible Person
                  </label>
                  <input 
                    value={responsiblePerson} 
                    onChange={(e) => setResponsiblePerson(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all bg-white"
                    placeholder="Enter responsible person's name"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-gray-600" />
                    Approved By
                  </label>
                  <input 
                    value={approvedBy} 
                    onChange={(e) => setApprovedBy(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all bg-white"
                    placeholder="Enter approver's name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div>
            {/* Additional Information Section */}
            <div className="bg-white rounded-lg p-4 border border-gray-300 h-full">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-900" />
                Additional Information
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block">
                    Parameters
                  </label>
                  <input 
                    value={parameters} 
                    onChange={(e) => setParameters(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all bg-white"
                    placeholder="e.g., Range: 0-100°C, Accuracy: ±0.1°C"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-900 mb-1 block">
                    Remarks
                  </label>
                  <textarea 
                    value={remark} 
                    onChange={(e) => setRemark(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all bg-white resize-none"
                    placeholder="Add any additional notes or comments..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="bg-white border-t border-gray-300 px-6 py-3 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={handleReset}
          disabled={!hasChanges || saving}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Reset Changes
        </button>
        
        <div className="flex gap-3">
          <button 
            type="button" 
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button 
            type="button"
            onClick={handleSubmit}
            disabled={saving || !hasChanges}
            className="px-6 py-2 text-sm font-medium text-white bg-black hover:bg-gray-800 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockEditForm;